import { tw } from "@careinfo/tailwind";
import { ColumnDef, type Table } from "@tanstack/react-table";
import moment from "moment";
import { Dispatch, SetStateAction } from "react";
import { useSearchParams } from "react-router-dom";
import NACell from "../Reusable/DataTable/NACell";
import TrackingCell from "../Reusable/DataTable/TrackingCell";

interface Props {
  checkedAllItems: boolean;
  handleSelectAll: (table: Table<any>) => void;
  isSelected: (value: string, order: string) => boolean;
  handleIndividualSelect: (value: string, order: string) => void;
  setSelectedOrder: Dispatch<SetStateAction<string>>;
  setOpenReviewModal: Dispatch<SetStateAction<boolean>>;
}

export const getManifestItemColumn = ({
  checkedAllItems,
  handleSelectAll,
  isSelected,
  handleIndividualSelect,
}: Props): ColumnDef<any, any>[] => {
  const [searchParams] = useSearchParams();
  const isorder = searchParams.get("isorder") || "order";

  const baseColumns: ColumnDef<any, any>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={checkedAllItems}
          onChange={() => handleSelectAll(table)}
          className={tw`cursor-pointer`}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={isSelected(row?.original?.id, row?.original?.order?.id)}
          onChange={() =>
            handleIndividualSelect(row?.original?.id, row?.original?.order?.id)
          }
          className={tw`cursor-pointer`}
        />
      ),
    },
    {
      header: "Date",
      accessorKey: "order.created",
     cell: ({ cell: { getValue } }) => {
    const value = getValue();
    if (!value) return <p>N/A</p>;

    const date = moment(value).format("YY-MM-DD");
    const time = moment(value).format("HH:mm:ss");

    return (
      <div className={tw`flex flex-col`}>
        <span className={tw`whitespace-nowrap`}>{date}</span>
        <span>{time}</span>
      </div>
    );
  },
    },
    {
      header: "Waybill",
      accessorKey: "order.tracking_number",
      cell: TrackingCell,
    },
    {
      header: "Origin",
      accessorKey: "order.pickup_branch.name",
      cell: NACell,
    },
    {
      header: "Sender",
      accessorKey: "order.sender.full_name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p>{getValue()}</p>
            <p className={tw`capitalize w-52`}>
              {row?.original?.order?.sender?.address}
            </p>
          </>
        );
      },
    },
    {
      header: "Receiver",
      accessorKey: "order.receiver.full_name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p>{getValue()}</p>
            <p className={tw`capitalize w-52`}>
              {row?.original?.order?.receiver?.address}
            </p>
          </>
        );
      },
    },
    {
      header: "Payment Method",
      accessorKey: "order.payment_method.name",
      cell: NACell,
    },
  ];

  // Conditionally add "Piece" column if isorder !== 'order'
  if (isorder === "order") {
    baseColumns.push({
      header: "Piece",
      accessorKey: "count",
      cell: ({ row, getValue }) => (
        <p>
          {getValue()}/
          <span className={tw`font-semibold`}>
            {row?.original?.order?.piece ?? 0}
          </span>
        </p>
      ),
    });
  }

  // Add final Received column
  baseColumns.push({
    header: "Received",
    accessorKey: "is_received",
    cell: ({ cell: { getValue } }) => (
      <>
        {getValue() ? (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86M22 4L12 14.01L9.00001 11.01"
              stroke="#26C943"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 9L9 15M9 9L15 15M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
              stroke="red"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </>
    ),
  });

  return baseColumns;
};
